import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from 'axios';
import css from './index.module.css';
import { isMobile } from "react-device-detect";

import SEO from "@components/seo";
import MainContent from "@components/content";

import { useTranslation } from 'react-i18next';
import * as Chart from './charts';
import * as Popups from './popups';

import * as Types from '../../types';
import { toast } from "sonner";
import Tabs from "@components/tabs";
import useUserData from "@hooks/useUserData";

export default function Container() {
    let { id } = useParams();
    const { t } = useTranslation();
    const { userData } = useUserData();
    const [step, setStep] = useState(4);
    const [container, setContainer] = useState<Types.Container>();
    const [statistics, setStatistics] = useState<any>({});
    const [statisticsLoading, setStatisticsLoading] = useState(true);
    const [statisticsError, setStatisticsError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [ipStatus, setIpStatus] = useState(false);
    const [ipStatusPre, setIpStatusPre] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showModalUpd, setShowModalUpd] = useState(false);
    const [showModalCode, setShowModalCode] = useState(false);
    const [showModalWH, setShowModalWH] = useState(false);
    const [update, setUpdate] = useState(false);

    useEffect(() => {
        if (!id || id === 'create') return;
        const controller = new AbortController();
        const config = { signal: controller.signal, timeout: 20000, headers: {
            Authorization: 'Bearer ' + localStorage.getItem('authToken')
        }};
        setStatistics({});
        setStatisticsError(false);
        setStatisticsLoading(true);
        async function load() {
            try {
                const response = await axios.get(`${import.meta.env.VITE_API_URL}/gtm/${id}`, config);
                setContainer(response.data);
                setStep(response.data.status);
                if (response.data.status < 4) return;
                const stats = await axios.get(`${import.meta.env.VITE_API_URL}/gtm/${id}/statistics`, config);
                const result: any = {};
                for (const key of ['cpu', 'disk', 'ram']) {
                    const series = stats.data[key];
                    const points = Array.isArray(series) ? series : series?.statistic;
                    if (!Array.isArray(points)) throw new Error('Invalid statistics response');
                    result[key] = { statistic: points };
                }
                setStatistics(result);
            } catch (error) {
                if (!controller.signal.aborted) setStatisticsError(true);
            } finally {
                if (!controller.signal.aborted) {
                    setStatisticsLoading(false);
                    setLoading(false);
                }
            }
        }
        void load();
        return () => controller.abort();
    }, [id, update, showModalWH]);

    function checkIp(notify = false) {
        const data = {
            domain: container?.domains.find(i => i.type === 0)?.value, 
            domainPre: container?.domains.find(i => i.type === 1)?.value
        }
        
        axios.post(`${import.meta.env.VITE_API_URL}/gtm/checkDns/${container?.id}`, data, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + localStorage.getItem('authToken')
        }}).then(response => {     
            setIpStatus(response.data.domain);
            setIpStatusPre(response.data.domainPre);

            if (notify) toast.success(t('gtm.updated'));
            setLoading(false);
        }).catch(error => {
            if (notify) toast.success(t('gtm.updated'));
            setLoading(false);
            setIpStatus(false);
        });
    }   

    function enterNext() {
        // @ts-ignore
        window.dataLayer = window.dataLayer || [];
        // @ts-ignore
        window.dataLayer.push({'event':'purchase',
            'userID': userData?.id,
            'step': 3
        });

        axios.post(`${import.meta.env.VITE_API_URL}/gtm/create/init/${id}`, [], {
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + localStorage.getItem('authToken')
            }
        }).then(response => {       
            setStep(3);
            toast.success(t('gtm.updated'));
        }).catch(error => {
            toast.error(t('gtm.on'));
        });
    }

  return (
    <MainContent>    
      <SEO 
        title="Контейнер"
        description=""
        ogUrl="https://artcons.io/"
      />

      <Popups.CodeInfo showModal={showModalCode} setShowModal={setShowModalCode} info={container} />
      <Popups.DeleteContainer showModal={showModal} setShowModal={setShowModal} />
      <Popups.UpdateInformation showModal={showModalUpd} setShowModal={setShowModalUpd} />
      <Popups.CustomWebhook showModal={showModalWH} setShowModal={setShowModalWH} info={container} />
      
      <div className={css.head}>
        <Link to={`/`} className={css.back}><i className="fi fi-rr-arrow-small-left"></i> Вернуться</Link>
        <h1>Контейнер</h1>
        <a>{container?.status === 5 ? <i className={`fi fi-sr-check-circle ${css.status}`}></i> : <i className={`fi fi-rr-time-oclock ${css.status}`} style={{ color: '#0f51d4' }}></i>} {container?.status === 5 ? 'В сети' : 'Включение...'} · {container?.id}</a>
      </div>

      {step < 4 &&
            <div className={css.steps} style={{ marginTop: 55 }}>
                <div className={css.stepSuccess}>
                    <a>{t('gtm.step')} 1</a>
                </div>
                <div className={step == 2 ? css.stepActive : step > 2 ? css.stepSuccess : css.step}>
                    <a>{t('gtm.step')} 2</a>
                </div>
                <div className={step == 3 ? css.stepActive : step > 3 ? css.stepSuccess : css.step}>
                    <a>{t('gtm.step')} 3</a>
                </div>
            </div>
        }

        {step == 0 &&
        <>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <i className="fi fi-rr-loading"></i>
                <a style={{ fontSize: 18 }}>Устанавливаем сервер, пожалуйста, подождите...</a>
            </div>
            
            <button onClick={() => setUpdate(true)} type="submit" style={{ width: "100%", background: "#677add", marginTop: 20, padding: 8, color: "#fff" }}>{t('gtm.check')}</button>
        </>
        }

        {step === 2 &&
            <>
            <div style={{ display: "grid", gap: 5 }}>
                <h3>{t('gtm.dns')}</h3>
                <a style={{ fontWeight: 400 }}>{t('gtm.dnsp')}</a>
            </div>
            
            <table style={{ borderCollapse: 'collapse', position: 'relative', width: "-webkit-fill-available", marginTop: 20 }}>
                <thead>
                    <tr>
                        <th style={{ cursor: 'pointer', padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'left', paddingLeft: 0 }}>TYPE</th>
                        <th style={{ cursor: 'pointer', padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'left' }}>HOST</th>
                        <th style={{ cursor: 'pointer', padding: '10px', borderBottom: '1px solid #ddd', textAlign: 'left' }}>VALUE</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{container?.ips.find(i => i.type === 'ipv4') ? "A" : "AAAA"}</td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{container?.domains.find(i => i.type === 0)?.value}</td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{container?.ips.find(i => i.type === 'ipv4')?.ip} {ipStatus ? <i className="fi fi-rr-check-circle" style={{ float: "right", color: "#6edd67" }} /> : <i className="fi fi-rr-triangle-warning" style={{ float: "right", color: "#dd6767" }} />}</td>
                    </tr>

                    <tr>
                        <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{container?.ips.find(i => i.type === 'ipv6') ? "AAAA" : "A"}</td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{container?.domains.find(i => i.type === 0)?.value}</td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{container?.ips.find(i => i.type === 'ipv6')?.ip} {ipStatus ? <i className="fi fi-rr-check-circle" style={{ float: "right", color: "#6edd67" }} /> : <i className="fi fi-rr-triangle-warning" style={{ float: "right", color: "#dd6767" }} />}</td>
                    </tr>

                    <tr>
                        <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{container?.ips.find(i => i.type === 'ipv4') ? "A" : "AAAA"}</td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{container?.domains.find(i => i.type === 1)?.value}</td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{container?.ips.find(i => i.type === 'ipv4')?.ip} {ipStatusPre ? <i className="fi fi-rr-check-circle" style={{ float: "right", color: "#6edd67" }} /> : <i className="fi fi-rr-triangle-warning" style={{ float: "right", color: "#dd6767" }} />}</td>
                    </tr>

                    <tr>
                        <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{container?.ips.find(i => i.type === 'ipv6') ? "AAAA" : "A"}</td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{container?.domains.find(i => i.type === 1)?.value}</td>
                        <td style={{ padding: '10px', borderBottom: '1px solid #ddd' }}>{container?.ips.find(i => i.type === 'ipv6')?.ip} {ipStatusPre ? <i className="fi fi-rr-check-circle" style={{ float: "right", color: "#6edd67" }} /> : <i className="fi fi-rr-triangle-warning" style={{ float: "right", color: "#dd6767" }} />}</td>
                    </tr>
                </tbody>
            </table>

            {(!ipStatus || !ipStatusPre) ? <button onClick={() => checkIp(true)} type="submit" style={{ width: "100%", background: "#0f51d4", marginTop: 10, padding: 10 }}>{t('gtm.check')}</button> : <button onClick={enterNext} type="submit" style={{ width: "100%", background: "#0f51d4", marginTop: 10, padding: 10, color: "#fff" }}>{t('gtm.next')}</button>}
            </>
        }

        {step == 3 &&
        <>
            <div className={css.information}>
                <div className={css.icon}>
                    <i className="fi fi-rr-time-past"></i>
                </div>
                
                <div className={css.text}>
                    <b>Установка может занять некоторое время</b>
                    <a>Пожалуйста, подождите, процесс установки контейнера может занять до 72 часов. Мы уведомим вас, как только всё будет готово.</a>
                </div>
            </div>

            <div className={css.tableSettings}>
                <div className={css.tableHead}>
                    <i className="fi fi-rr-rectangle-code"></i>
                    <a>Процесс установки</a>
                </div>            

                <div className={css.tableItem}>
                    <div className={css.left}>
                        <i className="fi fi-sr-check-circle"></i>
                        <a>Waiting to start</a>
                    </div>

                    <a className={css.time}>2m</a>
                </div>

                <div className={css.tableItem}>
                    <div className={css.left}>
                        <i className="fi fi-sr-check-circle"></i>
                        <a>Spin up build environment</a>
                    </div>

                    <a className={css.time}>4s</a>
                </div>

                <div className={css.tableItem}>
                    <div className={css.left}>
                        <i className="fi fi-sr-check-circle"></i>
                        <a>Install dependencies</a>
                    </div>

                    <a className={css.time}>15s</a>
                </div>
                
                <div className={css.tableItem}>
                    <div className={css.left}>
                        <i className="fi fi-sr-check-circle"></i>
                        <a>Configure DNS</a>
                    </div>

                    <a className={css.time}></a>
                </div>

                <div className={css.tableItem}>
                    <div className={css.left}>
                        <i className="fi fi-sr-check-circle"></i>
                        <a>Prepare credentials</a>
                    </div>

                    <a className={css.time}>5s</a>
                </div>

                <div className={css.tableItem}>
                    <div className={css.left}>
                        <div className={css.loader}>
                            <i className="fi fi-rr-loading"></i>
                        </div>
                        <a>Running container</a>
                    </div>

                    <a className={css.time}>
                        {(() => {
                            const startTime = new Date(container?.createdAt).getTime();
                            const currentTime = Date.now();
                            const diffInSeconds = Math.floor((currentTime - startTime) / 1000);

                            if (diffInSeconds < 60) {
                                return `${diffInSeconds}s`;
                            } else if (diffInSeconds < 3600) {
                                return `${Math.floor(diffInSeconds / 60)}m`;
                            } else {
                                return `${Math.floor(diffInSeconds / 3600)}h`;
                            }
                        })()}
                    </a>
                </div>
            </div>
        </>
        }

        {step >= 4 && (page === 0 ?
        <>
            <div className={css.tabs}>
                <Tabs 
                    sections={[
                        {
                            id: 0,
                            name: 'Общая информация'
                        },
                        {
                            id: 1,
                            name: 'Надстройки'
                        }
                    ]}

                    active={page}
                    setActive={setPage}
                />
            </div>
            
            <div className={css.chartsContainer}>
                <div className={css.chartsGrid}>
                    {statisticsError && <button type="button" onClick={() => setUpdate(value => !value)}>Повторить загрузку</button>}
                    {statistics.cpu && statistics.cpu.statistic.length > 0 ?
                    <div className={css.chartCard}>
                        <div className={css.chartHeader}>
                            <div className={`${css.chartIndicator} ${css.chartIndicatorCpu}`}></div>
                            <h5 className={css.chartTitle}>
                                {t('gtm.cpu')}
                            </h5>
                        </div>
                        <Chart.CpuChart data={[...statistics.cpu.statistic].reverse()} />
                    </div>
                    :
                    <div className={css.chartCard}>
                        <div className={css.chartHeaderEmpty}>
                            <div className={`${css.chartIndicator} ${css.chartIndicatorEmpty}`}></div>
                            <h5 className={css.chartTitle}>
                                {t('gtm.cpu')}
                            </h5>
                        </div>
                        <a className={css.chartEmptyText}>{statisticsLoading ? 'Загружаем статистику…' : statisticsError ? 'Не удалось загрузить статистику. Попробуйте ещё раз.' : 'Статистика пока отсутствует'}</a>
                    </div>
                    }

                    {statistics.disk && statistics.disk.statistic.length > 0 ?
                        <div className={css.chartCard}>
                            <div className={css.chartHeader}>
                                <div className={`${css.chartIndicator} ${css.chartIndicatorDisk}`}></div>
                                <h5 className={css.chartTitle}>
                                    {t('gtm.disk')}
                                </h5>
                            </div>
                            <Chart.DiskChart data={[...statistics.disk.statistic].reverse()} />
                        </div>
                        :
                        <div className={css.chartCard}>
                            <div className={css.chartHeaderEmpty}>
                                <div className={`${css.chartIndicator} ${css.chartIndicatorEmpty}`}></div>
                                <h5 className={css.chartTitle}>
                                    {t('gtm.disk')}
                                </h5>
                            </div>
                            <a className={css.chartEmptyText}>{statisticsLoading ? 'Загружаем статистику…' : statisticsError ? 'Не удалось загрузить статистику. Попробуйте ещё раз.' : 'Статистика пока отсутствует'}</a>
                        </div>
                    }

                    {statistics.ram && statistics.ram.statistic.length > 0 ?
                        <div className={css.chartCard}>
                            <div className={css.chartHeader}>
                                <div className={`${css.chartIndicator} ${css.chartIndicatorRam}`}></div>
                                <h5 className={css.chartTitle}>
                                    {t('gtm.ram')}
                                </h5>
                            </div>
                            <Chart.RamChart data={[...statistics.ram.statistic].reverse()} />
                        </div>
                        :
                        <div className={css.chartCard}>
                            <div className={css.chartHeaderEmpty}>
                                <div className={`${css.chartIndicator} ${css.chartIndicatorEmpty}`}></div>
                                <h5 className={css.chartTitle}>
                                    {t('gtm.ram')}
                                </h5>
                            </div>
                            <a className={css.chartEmptyText}>{statisticsLoading ? 'Загружаем статистику…' : statisticsError ? 'Не удалось загрузить статистику. Попробуйте ещё раз.' : 'Статистика пока отсутствует'}</a>
                        </div>
                    } 
                </div>

                <div style={{ flex: 0.3 }}>
                    <div style={{ width: '100%', border: "1px solid #00000042", padding: 25, boxSizing: 'border-box', marginTop: 20, borderRadius: 20, height: 'fit-content' }}>
                        <a style={{ fontWeight: 600, fontSize: 17 }}>{t('gtm.information')}</a>

                        <div className={css.infoBlock}>
                            <a>{container?.ips.find(i => i.type === 'ipv4') ? "IPv4" : "IPv6"}</a>
                            <b>{container?.ips.find(i => i.type === 'ipv4')?.ip}</b>
                        </div>

                        <div className={css.infoBlock}>
                            <a>{container?.ips.find(i => i.type === 'ipv6') ? "IPv6" : "IPv4"}</a>
                            <b>{container?.ips.find(i => i.type === 'ipv6')?.ip}</b>
                        </div>
                        
                        <div className={css.infoBlock}>
                            <a>{t('gtm.domainName')}</a>
                            <b>{container?.domains.find(i => i.type === 0)?.value}</b>
                        </div>

                        <div className={css.btnDelete} onClick={() => setShowModalCode(true)} style={{ background: "#41a341", marginTop: 20, color: "#fff" }}>{t('gtm.installSite')}</div>
                        <div className={css.btnDelete} onClick={() => setShowModal(true)} style={{ marginTop: 5, color: "#fff" }}>{t('gtm.deteleBtn')}</div>
                    </div>
                </div>
            </div>
        </>
        : 
        <>
            <div className={css.tabs}>
                <Tabs 
                    sections={[
                        {
                            id: 0,
                            name: 'Общая информация'
                        },
                        {
                            id: 1,
                            name: 'Надстройки'
                        }
                    ]}

                    active={page}
                    setActive={setPage}
                />
            </div>
            
            <div className={css.nadList}>

                <div className={css.nadItem}>
                    <div className={css.icon}>
                        <i className="fi fi-rr-user"></i>
                    </div>

                    <div className={css.textBlock}>
                        <b>User ID</b>
                        <a>Генерация уникального серверного ID пользователя, для повышения точности трекинга без хранения PII. Передается в X-Hashed-User-ID.</a>
                    </div>

                    <div className={css.connected}>Активно</div>
                </div>

                <div className={css.nadItem}>
                    <div className={css.icon}>
                        <i className="fi fi-rr-memo-circle-check"></i>
                    </div>

                    <div className={css.textBlock}>
                        <b>User Agent</b>
                        <a>Передает "чистые" данные user-agent в X-Device-Mobile, X-Device-OS, X-Device-Browser</a>
                    </div>

                    <div className={css.connected}>Активно</div>
                </div>

                <div className={css.nadItem} onClick={() => setShowModalWH(true)}>
                    <div className={css.icon}>
                        <i className="fi fi-rr-globe"></i>
                    </div>

                    <div className={css.textBlock}>
                        <b>Custom webhook</b>
                        <a>Позволяет видеть ваши запросы webhook через sGTM контейнер</a>
                    </div>

                    {container?.addons?.webhookPreview ? <div className={css.connected}>Подключено</div> : <div className={css.available}>Можно подключить</div> }
                </div>

                <div className={css.nadItem}>
                    <div className={css.icon}>
                        <i className="fi fi-rr-code-simple"></i>
                    </div>

                    <div className={css.textBlock}>
                        <b>Custom Loader</b>
                        <a>Загрузка WebGTM c вашего поддомена и сервера</a>
                    </div>

                    <div className={css.notAvailable}>В разработке</div>
                </div>

                <div className={css.nadItem}>
                    <div className={css.icon}>
                        <i className="fi fi-rr-cookie"></i>
                    </div>

                    <div className={css.textBlock}>
                        <b>Cookie Keeper</b>
                        <a>Продлевает жизнь cookies на более длительный срок</a>
                    </div>

                    <div className={css.notAvailable}>В разработке</div>
                </div>
            </div>
        </>
        )}
    </MainContent>
  )
}